import { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { object, string, boolean } from 'yup';

import isEmpty from 'lodash/isEmpty';

import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  useCreateTutorialQuestion,
  useUpdateTutorialQuestion,
  type TutorialQuestionParamsType,
} from 'src/pages/TutorialDetailsPage/api';
import Dropdown from 'src/components/Dropdown';
import TextInput from 'src/components/TextInput';
import ConfirmModal from 'src/components/ConfirmModal';
import { PencilAdornment } from 'src/utils/components';
import { useGetTutorials } from 'src/pages/SetupPage/api';

import messages from './messages';
import TutorialOptionForm from '../TutorialOptionForm';

interface TutorialQuestionFormProps {
  create: boolean;
  tutorialQuestion?: UCM.TutorialQuestionType;
}

export default function TutorialQuestionForm({
  create,
  tutorialQuestion,
}: TutorialQuestionFormProps) {
  const { formatMessage } = useIntl();
  const { uuid: tutorialUuid } = useParams<{ uuid: string }>();

  const [open, setOpen] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const { data: tutorials } = useGetTutorials();

  const { mutate: createTutorialQuestion, isPending: isCreatingQuestion } =
    useCreateTutorialQuestion();
  const { mutate: updateTutorialQuestion, isPending: isUpdatingQuestion } =
    useUpdateTutorialQuestion();

  const booleanOptions = useMemo(
    () => [
      { value: true, label: formatMessage(messages.yesLabel) },
      { value: false, label: formatMessage(messages.noLabel) },
    ],
    [],
  );

  const formattedTutorialOptions = useMemo(() => {
    return tutorials?.map((tutorial) => ({
      value: tutorial.uuid,
      label: tutorial.title.en,
    }));
  }, [tutorials]);

  const initialValues = useMemo(
    () => ({
      code: '',
      isVisible: false,
      questionEn: '',
      questionDe: '',
      descriptionEn: '',
      descriptionDe: '',
      correctAnswerExplanationEn: '',
      correctAnswerExplanationDe: '',
      tutorialUuid: tutorialUuid ?? '',
    }),
    [tutorialUuid],
  );

  const formik = useFormik<TutorialQuestionParamsType>({
    initialValues,
    enableReinitialize: true,
    validateOnChange: validateOnChange,
    validationSchema: object().shape({
      code: string().required(),
      tutorialUuid: string().required(),
      isVisible: boolean().required(),
      questionEn: string().required(),
      questionDe: string().required(),
      descriptionEn: string().required(),
      descriptionDe: string().required(),
      correctAnswerExplanationEn: string().required(),
      correctAnswerExplanationDe: string().required(),
    }),
    onSubmit: () => {},
  });

  const { values, errors } = formik;

  // Set the values of the form when the tutorial is opened in edit mode
  useEffect(() => {
    if (tutorialQuestion && !create && open) {
      formik.setValues({
        code: tutorialQuestion.code,
        tutorialUuid: tutorialQuestion.tutorialUuid,
        isVisible: tutorialQuestion.isVisible,
        questionEn: tutorialQuestion.question.en,
        questionDe: tutorialQuestion.question.de,
        descriptionEn: tutorialQuestion.description.en,
        descriptionDe: tutorialQuestion.description.de,
        correctAnswerExplanationEn:
          tutorialQuestion.correctAnswerExplanation.en,
        correctAnswerExplanationDe:
          tutorialQuestion.correctAnswerExplanation.de,
      });
    }
  }, [open, tutorialQuestion, create]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setValidateOnChange(false);
    formik.resetForm();
    setOpen(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    setValidateOnChange(true);

    const errors = await formik.validateForm();
    const isValid = isEmpty(errors);

    if (!isValid) return false;

    return new Promise<boolean>((resolve) => {
      const mutationOptions = {
        onSuccess: () => {
          resolve(true);
        },
        onError: () => {
          resolve(false);
        },
      };

      if (create) {
        createTutorialQuestion(values, mutationOptions);
      }

      if (!create && tutorialQuestion) {
        updateTutorialQuestion(
          { ...values, questionUuid: tutorialQuestion.uuid },
          mutationOptions,
        );
      }
    });
  }, [create, values, tutorialQuestion]);

  return (
    <ConfirmModal
      width="70%"
      onOpen={handleOpen}
      onClose={handleClose}
      onConfirm={handleSubmit}
      title={formatMessage(messages.title)}
      loading={isCreatingQuestion || isUpdatingQuestion}
      triggerButton={(handleOpen) =>
        create ? (
          <Button
            variant="outlined"
            onClick={handleOpen}
            startIcon={<AddOutlinedIcon />}
          >
            {formatMessage(messages.createQuestionButtonLabel)}
          </Button>
        ) : (
          <Tooltip arrow title={formatMessage(messages.editTooltip)}>
            <IconButton onClick={handleOpen}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="code"
            value={values.code}
            error={!!errors.code}
            helperText={errors.code}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.codeLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Dropdown
            fullWidth
            name="tutorialUuid"
            value={values.tutorialUuid}
            options={formattedTutorialOptions}
            onChange={formik.handleChange}
            helperText={errors.tutorialUuid}
            error={!!errors.tutorialUuid}
            label={formatMessage(messages.tutorialLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Dropdown
            fullWidth
            name="isVisible"
            value={values.isVisible}
            options={booleanOptions}
            onChange={formik.handleChange}
            helperText={errors.isVisible}
            error={!!errors.isVisible}
            label={formatMessage(messages.visibleLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="questionEn"
            value={values.questionEn}
            error={!!errors.questionEn}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.questionEnLabel)}
            helperText={
              errors.questionEn ?? formatMessage(messages.shownToUsers)
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="questionDe"
            value={values.questionDe}
            error={!!errors.questionDe}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.questionDeLabel)}
            helperText={
              errors.questionDe ?? formatMessage(messages.shownToUsers)
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            multiline
            name="descriptionEn"
            value={values.descriptionEn}
            error={!!errors.descriptionEn}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.descriptionEnLabel)}
            helperText={
              errors.descriptionEn ?? formatMessage(messages.shownToUsers)
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            multiline
            name="descriptionDe"
            value={values.descriptionDe}
            error={!!errors.descriptionDe}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.descriptionDeLabel)}
            helperText={
              errors.descriptionDe ?? formatMessage(messages.shownToUsers)
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            multiline
            name="correctAnswerExplanationEn"
            value={values.correctAnswerExplanationEn}
            error={!!errors.correctAnswerExplanationEn}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.correctAnswerExplanationEnLabel)}
            helperText={
              errors.correctAnswerExplanationEn ??
              formatMessage(messages.shownToUsers)
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            multiline
            name="correctAnswerExplanationDe"
            value={values.correctAnswerExplanationDe}
            error={!!errors.correctAnswerExplanationDe}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.correctAnswerExplanationDeLabel)}
            helperText={
              errors.correctAnswerExplanationDe ??
              formatMessage(messages.shownToUsers)
            }
          />
        </Grid>

        {!create && tutorialQuestion && (
          <TutorialOptionForm tutorialQuestion={tutorialQuestion} />
        )}
      </Grid>
    </ConfirmModal>
  );
}
